import {
  DaysOfWeek,
  ManagedAction,
  ManagedPolicyStatus,
  ParentGrantStatus,
  SettingsRequestStatus,
  SafetyAppealStatus
} from 'enums';
import { ALL_DAYS_OF_WEEK } from 'constants/daysOfWeek';
import { ManagedPolicyInput } from 'interfaces/managed-blocking.interface';

export const MANAGED_API_PREFIX = '/managed-blocking';
export const MANAGED_HISTORY_LIMIT = 100;
export const MANAGED_STUDENT_BATCH_LIMIT = 500;
export const MANAGED_YEAR_LABEL_LIMIT = 40;
export const MANAGED_CANONICAL_YEAR_MAX = 13;
export const MANAGED_REFRESH_MS = 30000;
/**
 * Day codes, not indexes. Habits and routines already use `DaysOfWeek` across the product, and a
 * managed window now speaks the same vocabulary — a stored `[1,2,3,4,5]` never said whether 1 was
 * Monday, and an off-by-one reads as a school policy that activates on the wrong days.
 *
 * Sunday-first, because the position is the weekday index the backend's overnight-window
 * arithmetic uses. That is a different ordering from {@link ALL_DAYS_OF_WEEK}, which is Monday-first
 * because it is what a person reads — so this is *derived* from it by rotation rather than
 * re-listed. Re-listing would mean two hand-maintained lists of the same seven tokens, which is the
 * duplication this change set out to remove.
 */
export const MANAGED_DAY_CODES: DaysOfWeek[] = [
  DaysOfWeek.SUN,
  ...ALL_DAYS_OF_WEEK.filter((day) => day !== DaysOfWeek.SUN)
];
export const MANAGED_DAY_LABEL_KEYS: Record<DaysOfWeek, string> = {
  [DaysOfWeek.SUN]: 'managed_blocking.sunday',
  [DaysOfWeek.MON]: 'managed_blocking.monday',
  [DaysOfWeek.TUE]: 'managed_blocking.tuesday',
  [DaysOfWeek.WED]: 'managed_blocking.wednesday',
  [DaysOfWeek.THU]: 'managed_blocking.thursday',
  [DaysOfWeek.FRI]: 'managed_blocking.friday',
  [DaysOfWeek.SAT]: 'managed_blocking.saturday'
};
export const MANAGED_INPUT_CLASS =
  'w-full rounded border border-primary-200 bg-white p-2 text-primary-900 dark:border-secondary-dark-500 dark:bg-secondary-dark-200 dark:text-secondary-dark-900 disabled:opacity-60';
export const MANAGED_POLICY_STATUS_KEYS: Record<ManagedPolicyStatus, string> = {
  [ManagedPolicyStatus.DRAFT]: 'managed_blocking.draft',
  [ManagedPolicyStatus.PUBLISHED]: 'managed_blocking.published'
};
export const MANAGED_ACTION_KEYS: Record<ManagedAction, string> = {
  [ManagedAction.ALLOW]: 'managed_blocking.allow',
  [ManagedAction.BLOCK]: 'managed_blocking.block'
};
export const MANAGED_GRANT_STATUS_KEYS: Record<ParentGrantStatus, string> = {
  [ParentGrantStatus.PENDING]: 'managed_blocking.pending',
  [ParentGrantStatus.ACCEPTED]: 'managed_blocking.accepted',
  [ParentGrantStatus.REVOKED]: 'managed_blocking.revoked'
};
export const MANAGED_REQUEST_STATUS_KEYS: Record<
  SettingsRequestStatus,
  string
> = {
  [SettingsRequestStatus.PENDING]: 'managed_blocking.pending',
  [SettingsRequestStatus.APPROVED]: 'managed_blocking.approved',
  [SettingsRequestStatus.PROPOSED]: 'managed_blocking.proposed',
  [SettingsRequestStatus.COMMITTED]: 'managed_blocking.committed',
  [SettingsRequestStatus.REJECTED]: 'managed_blocking.rejected'
};
export const MANAGED_APPEAL_STATUS_KEYS: Record<SafetyAppealStatus, string> = {
  [SafetyAppealStatus.UPHOLD]: 'managed_blocking.uphold_block',
  [SafetyAppealStatus.ALLOW]: 'managed_blocking.allow_scoped_access',
  [SafetyAppealStatus.REVIEW]: 'managed_blocking.needs_human_review'
};
export const MANAGED_FIELD_KEYS: Record<keyof ManagedPolicyInput, string> = {
  name: 'managed_blocking.name',
  time_zone: 'managed_blocking.time_zone',
  is_enabled: 'managed_blocking.enabled',
  windows: 'managed_blocking.windows',
  blocked_categories: 'managed_blocking.categories',
  allowlist: 'managed_blocking.allowlist',
  excluded_dates: 'managed_blocking.excluded_dates',
  unknown_action: 'managed_blocking.unknown_action',
  unavailable_action: 'managed_blocking.unavailable_action'
};
export const NEW_MANAGED_POLICY: ManagedPolicyInput = {
  name: '',
  time_zone: 'Australia/Melbourne',
  is_enabled: true,
  windows: [
    {
      days_of_week: [
        DaysOfWeek.MON,
        DaysOfWeek.TUE,
        DaysOfWeek.WED,
        DaysOfWeek.THU,
        DaysOfWeek.FRI
      ],
      start_time: '08:30',
      end_time: '15:30'
    }
  ],
  blocked_categories: [],
  allowlist: [],
  excluded_dates: [],
  unknown_action: ManagedAction.BLOCK,
  unavailable_action: ManagedAction.BLOCK
};

export const MANAGED_DECISION_REASON_KEYS: Record<string, string> = {
  inactive: 'managed_blocking.result_inactive',
  policy_allowlist: 'managed_blocking.result_allowlist',
  blocked_category: 'managed_blocking.result_category',
  classification_unavailable: 'managed_blocking.result_unavailable',
  classification_unknown: 'managed_blocking.result_unknown',
  categories_allowed: 'managed_blocking.result_categories_allowed'
};

export const MANAGED_ACTOR_KEYS: Record<string, string> = {
  school: 'managed_blocking.actor_school',
  parent: 'managed_blocking.actor_parent',
  system: 'managed_blocking.actor_system'
};
export const MANAGED_HISTORY_ACTION_KEYS: Record<string, string> = {
  policy_created: 'managed_blocking.policy_created',
  policy_updated: 'managed_blocking.policy_updated',
  policy_published: 'managed_blocking.policy_published',
  assignment_updated: 'managed_blocking.assignment_updated',
  assignment_removed: 'managed_blocking.assignment_removed'
};

export const MANAGED_CATEGORY_LABEL_KEYS: Record<string, string> = {
  adult: 'managed_blocking.category_labels.adult',
  auctions: 'managed_blocking.category_labels.auctions',
  browser_runtime_attack:
    'managed_blocking.category_labels.browser_runtime_attack',
  command_and_control: 'managed_blocking.category_labels.command_and_control',
  compromised_website: 'managed_blocking.category_labels.compromised_website',
  copyright_infringement:
    'managed_blocking.category_labels.copyright_infringement',
  cryptocurrency: 'managed_blocking.category_labels.cryptocurrency',
  dating: 'managed_blocking.category_labels.dating',
  dynamic_dns: 'managed_blocking.category_labels.dynamic_dns',
  encrypted_dns: 'managed_blocking.category_labels.encrypted_dns',
  extremism: 'managed_blocking.category_labels.extremism',
  gambling: 'managed_blocking.category_labels.gambling',
  games: 'managed_blocking.category_labels.games',
  grayware: 'managed_blocking.category_labels.grayware',
  hacking: 'managed_blocking.category_labels.hacking',
  high_risk: 'managed_blocking.category_labels.high_risk',
  insufficient_content: 'managed_blocking.category_labels.insufficient_content',
  internet_portals: 'managed_blocking.category_labels.internet_portals',
  malware: 'managed_blocking.category_labels.malware',
  medium_risk: 'managed_blocking.category_labels.medium_risk',
  newly_registered_domains:
    'managed_blocking.category_labels.newly_registered_domains',
  not_resolved: 'managed_blocking.category_labels.not_resolved',
  parked: 'managed_blocking.category_labels.parked',
  peer_to_peer: 'managed_blocking.category_labels.peer_to_peer',
  phishing: 'managed_blocking.category_labels.phishing',
  private_ip_addresses: 'managed_blocking.category_labels.private_ip_addresses',
  proxy_avoidance_and_anonymizers:
    'managed_blocking.category_labels.proxy_avoidance_and_anonymizers',
  questionable: 'managed_blocking.category_labels.questionable',
  ransomware: 'managed_blocking.category_labels.ransomware',
  real_time_detection: 'managed_blocking.category_labels.real_time_detection',
  remote_access: 'managed_blocking.category_labels.remote_access',
  scanning_activity: 'managed_blocking.category_labels.scanning_activity',
  shareware_and_freeware:
    'managed_blocking.category_labels.shareware_and_freeware',
  social_networking: 'managed_blocking.category_labels.social_networking',
  streaming_media: 'managed_blocking.category_labels.streaming_media',
  unknown: 'managed_blocking.category_labels.unknown',
  weapons: 'managed_blocking.category_labels.weapons',
  web_advertisements: 'managed_blocking.category_labels.web_advertisements',
  web_based_email: 'managed_blocking.category_labels.web_based_email'
};
